import { PaginationRange } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { queryOptionsGetTasksPaginated } from '../../../../api/task/taskApi';
import { queryOptionsGetTitlesPaginated } from '../../../../api/titles/titlesApi';
import {
  AppPagination,
  AppPaginationFooter,
} from '../../../../components/AppPagination';
import { EntityList } from '../../../../components/EntityList';
import { ErrorFallback } from '../../../../components/QueryResults/ErrorFallback';
import {
  TaskCard,
  buildTaskCardViewUrl,
} from '../../../../components/TaskCard';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { usePaginationBounds } from '../../../../hooks/usePaginationBounds';
import { useSetSearchParam } from '../../../../hooks/useSetSearchParam';
import { useTaskPartyNames } from '../../../../hooks/useTaskPartyNames';
import { WorkspaceTask } from '../../../../types/taskTypes';

const PAGE_PARAM = 'clientTasksPage';
const TASKS_DISPLAY_PAGE_SIZE = 4;

// Each id becomes its own `titleIds=` query param (~46 bytes), and API Gateway
// caps the request line plus headers at 10KB, auth header included. 100 ids
// keeps the request under ~5KB. Stays below the backend's MAX_TITLE_IDS
// (packages/backend/apps/tasks/src/task/dto/task-search-params.dto.ts).
// Clients with more titles than this get the truncation note below rather
// than a silently incomplete list.
const CLIENT_TITLE_IDS_PAGE_SIZE = 100;

interface ClientTasksSectionProps {
  clientId: string;
}

/**
 * MSR3 Clients Summary "Tasks" card (S4). Resolves the client's title ids,
 * then filters `GET /task` server-side via the real `titleIds` param
 * (APP-2180 B2) — the two-hop client-side stopgap this used before the SDK
 * regen is gone; see docs/epics/msr3-clients-page/decisions/decisions.md
 * ADR-CLP-007 for that history.
 *
 * The title-id resolution is still its own hop (`Task` has no `clientId`,
 * only `titleId`), capped at `CLIENT_TITLE_IDS_PAGE_SIZE` — the one
 * remaining limit, unlike the removed workspace-wide task-count cap. When the
 * client has more titles than that, the section says so instead of quietly
 * dropping the rest.
 *
 * Backend treats an empty `titleIds` array the same as "no filter" (matching
 * `toFilterArg`'s drop-empty-array convention elsewhere in this codebase),
 * so a client with zero titles must short-circuit here rather than send
 * `titleIds: []` through — that would return the whole workspace's tasks.
 *
 * Cards get the same competing-claim Other Claimant name and draft Delete
 * as on Title Summary and the Tasks page. They leave out the task's own
 * client, because every task here belongs to the page's client
 * (ADR-CLP-002). Delete has no `onDeleted`: unlike Title Summary, this page
 * is still valid after a draft goes, and the titles query refetch drops the
 * draft's title id from the tasks filter.
 */
export const ClientTasksSection = ({ clientId }: ClientTasksSectionProps) => {
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const setParam = useSetSearchParam();
  const [activeWorkspace] = useActiveWorkspace();
  const workspaceId = activeWorkspace?._id;

  const page = Math.max(1, Number(searchParams.get(PAGE_PARAM)) || 1);
  const setPage = (next: number) => setParam(PAGE_PARAM, String(next));

  const titlesQuery = useQuery(
    queryOptionsGetTitlesPaginated({
      ownerClientIds: [clientId],
      pageSize: CLIENT_TITLE_IDS_PAGE_SIZE,
      workspaceId,
    }),
  );
  const titleIds = useMemo(
    () => (titlesQuery.data?.items ?? []).map(({ _id }) => _id),
    [titlesQuery.data],
  );
  const hasNoTitles = titlesQuery.isSuccess && titleIds.length === 0;
  const totalTitles = titlesQuery.data?.meta?.totalItems ?? 0;
  const isTitleListTruncated = totalTitles > titleIds.length;

  const tasksQuery = useQuery({
    ...queryOptionsGetTasksPaginated({
      page,
      pageSize: TASKS_DISPLAY_PAGE_SIZE,
      titleIds,
      workspaceId,
    }),
    enabled: titleIds.length > 0,
  });

  const tasks = (tasksQuery.data?.items as WorkspaceTask[] | undefined) ?? [];
  const { getPartyNames } = useTaskPartyNames(tasks, workspaceId);
  const totalItems = tasksQuery.data?.meta?.totalItems ?? 0;
  const totalPages = tasksQuery.data?.meta?.totalPages ?? 0;

  const { clampedPage } = usePaginationBounds({
    isSuccess: tasksQuery.isSuccess,
    page,
    setPage,
    totalItems,
    totalPages,
  });

  // Same silently-render-nothing treatment TitleSummaryV3 gives its own
  // Tasks section — it's a secondary section, not the page's own gate.
  if (titlesQuery.isPending) return null;
  if (titlesQuery.isError) return <ErrorFallback error={titlesQuery.error} />;
  if (hasNoTitles) return null;
  if (tasksQuery.isPending) return null;
  if (tasksQuery.isError) return <ErrorFallback error={tasksQuery.error} />;
  if (tasks.length === 0 && !isTitleListTruncated) return null;

  return (
    <EntityList>
      <EntityList.Header>
        <EntityList.Title>Tasks</EntityList.Title>
        <EntityList.Count count={totalItems} />

        {totalPages > 1 && (
          <EntityList.Actions>
            <PaginationRange
              page={clampedPage}
              pageSize={TASKS_DISPLAY_PAGE_SIZE}
              totalItems={totalItems}
            />

            <AppPagination
              currentPage={clampedPage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </EntityList.Actions>
        )}
      </EntityList.Header>

      {isTitleListTruncated && (
        <p className="text-sm text-muted-foreground">
          Showing tasks for the first {titleIds.length} of {totalTitles} titles.
        </p>
      )}

      <EntityList.Content>
        {tasks.map((task) => {
          const { isOtherClaimantNamePending, otherClaimantName } =
            getPartyNames(task);

          return (
            <TaskCard
              key={task._id}
              isOtherClaimantNamePending={isOtherClaimantNamePending}
              otherClaimantName={otherClaimantName}
              task={task}
              type={task.type}
              viewUrl={buildTaskCardViewUrl(task, task.type, search)}
              workspaceId={workspaceId}
            />
          );
        })}
      </EntityList.Content>

      {totalPages > 1 && (
        <AppPaginationFooter
          currentPage={clampedPage}
          pageSize={TASKS_DISPLAY_PAGE_SIZE}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </EntityList>
  );
};
